import { forwardRef, type ButtonHTMLAttributes } from "react";
import { Loader2 } from "lucide-react";

import { cn } from "@/lib/cn";

type Variant = "primary" | "secondary" | "ghost" | "danger" | "subtle";
type Size = "sm" | "md" | "lg";

const VARIANTS: Record<Variant, string> = {
  // The brand orange means "the main action here" and nothing else, so an
  // orange button is always the thing to press.
  primary: "bg-brand text-ink-inverse hover:bg-brand-hover active:bg-brand-pressed font-medium",
  secondary:
    "bg-surface-raised text-ink border border-border hover:bg-surface-hover hover:border-border-strong",
  ghost: "text-ink-muted hover:bg-surface-hover hover:text-ink",
  danger: "bg-danger text-white hover:brightness-110 font-medium",
  subtle: "bg-brand-subtle text-brand border border-brand/30 hover:border-brand/60",
};

const SIZES: Record<Size, string> = {
  // Touch targets stay at least 36 px tall: this gets used with gloves on.
  sm: "h-9 px-3 text-[13px] gap-1.5",
  md: "h-10 px-4 text-sm gap-2",
  lg: "h-12 px-5 text-[15px] gap-2",
};

export interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: Variant;
  size?: Size;
  loading?: boolean;
  fullWidth?: boolean;
}

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(function Button(
  {
    variant = "secondary",
    size = "md",
    loading = false,
    fullWidth,
    className,
    children,
    disabled,
    ...props
  },
  ref,
) {
  return (
    <button
      ref={ref}
      disabled={disabled || loading}
      className={cn(
        "inline-flex items-center justify-center rounded-md transition-colors",
        "select-none whitespace-nowrap disabled:cursor-not-allowed disabled:opacity-50",
        VARIANTS[variant],
        SIZES[size],
        fullWidth && "w-full",
        className,
      )}
      {...props}
    >
      {loading && <Loader2 className="size-4 animate-spin" aria-hidden />}
      {children}
    </button>
  );
});

/** A square icon-only button. Keeps the 40 px touch target. */
export const IconButton = forwardRef<HTMLButtonElement, ButtonProps & { label: string }>(
  function IconButton({ label, className, size = "md", ...props }, ref) {
    return (
      <Button
        ref={ref}
        aria-label={label}
        title={label}
        size={size}
        className={cn(size === "sm" ? "w-9 px-0" : "w-10 px-0", className)}
        {...props}
      />
    );
  },
);
